import React, { useState } from 'react';
import { Form, Input, Select } from 'antd';
import { useDataProvider, useNotify, usePermissions, useRedirect } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { DurationInput, Field, FormSection, NetorcaForm, useServerErrors } from '../Common/Form';
import { EntityFormRoute, HttpRequestFields, ServiceField } from '../Common/Integrations';
import MainCard from '../Common/MainCard/MainCard';
import DeleteButton from '../Common/DeleteButton';
import { CHANGE_INSTANCE_STATES } from './config';
import WebhookHelp from './WebhookHelp';

/** stands in for "no state" in the select; stored as null */
const NO_STATE = '__none__';

/** WebhookSerializer.Meta.fields — anything else the API rejects is a general error */
const WEBHOOK_FIELDS = [
    'name',
    'description',
    'target_url',
    'authorization',
    'service',
    'active',
    'trust_env',
    'verify',
    'timeout',
    'change_instance_state',
    'trigger_wait_time',
    'trigger_cooling_time',
];

const WebhookForm = ({ record }: { record?: any }) => {
    const [form] = Form.useForm();
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const { apply, fieldErrors, clear } = useServerErrors(form, WEBHOOK_FIELDS);
    const [saving, setSaving] = useState(false);

    const isAdmin = permissions === 'ADMIN';
    const editing = !!record;

    const onFinish = (values: any) => {
        clear();
        setSaving(true);

        // The API takes a PUT, so a field the form does not show still has to
        // travel with the payload or it falls back to the model default.
        // `active` is owned by the list page; `trust_env` is not supported yet.
        const payload = {
            ...values,
            active: editing ? record.active : true,
            trust_env: editing ? record.trust_env : true,
        };

        const request = editing
            ? dataProvider.update('webhooks', { id: record.id, data: payload, previousData: { id: record.id } })
            : dataProvider.create('webhooks', { data: payload });

        request
            .then(() => {
                notify(editing ? 'Webhook updated' : 'Webhook created');
                redirect('/webhooks');
            })
            .catch((error: any) => {
                apply(error, editing ? 'Could not update the webhook' : 'Could not create the webhook');
                setSaving(false);
            });
    };

    return (
        <NetorcaForm
            className="nf-form--sheet"
            form={form}
            name="webhook"
            /* no eyebrow: the arrow beside the name already says what list it
               belongs to, and the name is what the page is about */
            title={editing ? record.name : 'New webhook'}
            onBack={() => redirect('/webhooks')}
            backLabel="Back to webhooks"
            help={{ title: 'How webhooks work', content: <WebhookHelp /> }}
            readOnly={!isAdmin}
            submitLabel={editing ? 'Save changes' : 'Create webhook'}
            submitting={saving}
            onCancel={() => redirect('/webhooks')}
            onFinish={onFinish}
            serverErrors={fieldErrors}
            destructive={
                editing && isAdmin ? <DeleteButton title="Webhook" resource="webhooks" record={record} /> : undefined
            }
            initialValues={
                record
                    ? { ...record, service: record.service?.id }
                    : {
                          verify: true,
                          timeout: 20,
                          trigger_wait_time: 5,
                          trigger_cooling_time: 5,
                          change_instance_state: null,
                      }
            }
        >
            {/* One grid for the whole form, read top to bottom: what the
                webhook is, where it calls, what makes it fire. Widths follow
                what each control actually holds — a URL gets a row, a number
                gets a quarter of one — and every row is filled, so the order
                alone carries the grouping and nothing has to rule it off. */}
            <FormSection id="webhook" title="Webhook" bare>
                <Field
                    name="name"
                    label="Name"
                    span={6}
                    rules={[{ required: true, message: 'Give the webhook a name' }]}
                >
                    <Input placeholder="Provisioning notifier" />
                </Field>

                <ServiceField span={6} />

                {/* two lines tall, so it takes the row rather than leaving the
                    fields beside it standing over empty space */}
                <Field
                    name="description"
                    label="Description"
                    rules={[{ max: 200, message: 'Keep the description under 200 characters' }]}
                >
                    <Input.TextArea rows={2} maxLength={200} showCount placeholder="What this webhook is for" />
                </Field>

                <HttpRequestFields />

                <Field
                    name="change_instance_state"
                    label="Fire on state"
                    span={4}
                    hint="With None, the webhook fires when a change instance is created. One webhook per service per state."
                    // rc-select refuses a null option value, so None travels as a
                    // sentinel through the control and lands in the form as null
                    getValueProps={(value) => ({ value: value ?? NO_STATE })}
                    normalize={(value) => (value === NO_STATE ? null : value)}
                >
                    <Select
                        options={[
                            { value: NO_STATE, label: 'None — on creation' },
                            ...CHANGE_INSTANCE_STATES.map((state) => ({ value: state, label: state })),
                        ]}
                    />
                </Field>

                <Field
                    name="trigger_wait_time"
                    label="Wait time"
                    span={4}
                    hint="Events arriving within this window are batched into one call."
                    rules={[{ required: true, message: 'Wait time must be at least 5 seconds' }]}
                >
                    <DurationInput min={5} />
                </Field>

                <Field
                    name="trigger_cooling_time"
                    label="Cool-down"
                    span={4}
                    hint="No further call is made until this long after the last one."
                    rules={[{ required: true, message: 'Cool-down must be at least 5 seconds' }]}
                >
                    <DurationInput min={5} />
                </Field>
            </FormSection>
        </NetorcaForm>
    );
};

const WebhookCreate = (props: any) => (
    <EntityFormRoute
        id={props.id}
        resource="webhooks"
        basePath="/webhooks"
        render={(record) => (
            <MainCard>
                <WebhookForm record={record} />
            </MainCard>
        )}
    />
);

export default WebhookCreate;
